/**
 * Redux store configuration
 * Integrates all slices for state management
 */

import { configureStore } from '@reduxjs/toolkit';
import { enableMapSet } from 'immer';
import { useDispatch, useSelector, useStore, TypedUseSelectorHook } from 'react-redux';

// Enable Immer support for Set/Map used in analysisSlice (pendingFields, permanentlyExtractedFields)
enableMapSet();
import userSlice from './userSlice';
import sessionSlice from './sessionSlice';
import recordingSlice from './recordingSlice';
import transcriptSlice from './transcriptSlice';
import analysisSlice from './analysisSlice';
import toastsSlice from './toastsSlice';
import configSlice from './configSlice';
import profileSlice from './profileSlice';
import siteSlice from './siteSlice';
import { loggerToUILoggingMiddleware } from '../logging/middleware';
import { navigatorUiLogger } from '../logging/runtime';

export const reducers = {
  user: userSlice,
  session: sessionSlice,
  recording: recordingSlice,
  transcript: transcriptSlice,
  analysis: analysisSlice,
  toasts: toastsSlice,
  config: configSlice,
  profile: profileSlice,
  site: siteSlice,
};

const store = configureStore({
  reducer: reducers,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these paths for serializability checks
        // (e.g., Sets in analysisSlice)
        ignoredPaths: ['analysis.pendingFields', 'analysis.permanentlyExtractedFields'],
        ignoredActions: [
          'analysis/setPendingFields',
          'analysis/addPendingField',
          'analysis/removePendingField',
          'analysis/markFieldAsExtracted',
        ],
      },
    }).concat(loggerToUILoggingMiddleware(navigatorUiLogger)),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppStore = () => useStore<RootState>();

export default store;
